import React, { useEffect, useState } from 'react';
import { Car, ZoomIn } from 'lucide-react';
import { vehiclesAPI, uploadsAPI } from '../services/api';

let cache = null;
let pending = null;
const loadLogos = () => {
  if (cache) return Promise.resolve(cache);
  if (!pending) pending = vehiclesAPI.getBrandLogos().then(l => { cache = l; return l; }).catch(() => ({}));
  return pending;
};

// Whole map { BRAND: url } (resolved), for lists rendering many logos
export const useVehicleBrandLogos = () => {
  const [logos, setLogos] = useState(cache || {});
  useEffect(() => { if (!cache) loadLogos().then(setLogos); }, []);
  return logos;
};

export const useVehicleBrandLogo = (brand) => {
  const [logos, setLogos] = useState(cache || {});
  useEffect(() => { if (!cache) loadLogos().then(setLogos); }, []);
  const url = logos[(brand || '').trim().toUpperCase()];
  return url ? uploadsAPI.resolveUrl(url) : null;
};

const modelCache = {};
const modelPending = {};
const loadModelImages = (brand) => {
  if (modelCache[brand]) return Promise.resolve(modelCache[brand]);
  if (!modelPending[brand]) modelPending[brand] = vehiclesAPI.getModelImages(brand).then(i => { modelCache[brand] = i; return i; }).catch(() => ({}));
  return modelPending[brand];
};

// Picture of the exact model (admin upload), only fetched when `enabled`
export const useVehicleModelImage = (brand, model, enabled = true) => {
  const [images, setImages] = useState(() => (brand && modelCache[brand]) || {});
  useEffect(() => { if (enabled && brand && model && !modelCache[brand]) loadModelImages(brand).then(setImages); else if (brand && modelCache[brand]) setImages(modelCache[brand]); }, [brand, model, enabled]);
  const url = model ? images[model] : null;
  return url ? uploadsAPI.resolveUrl(url) : null;
};

// Round badge showing the vehicle make logo; falls back to the model picture, then to a car icon. `bare` = small inline image without the circle
// Large preview that appears on hover / focus (no click needed), anchored under the thumbnail
const HoverPreview = ({ src, title, testId }) => (
  <div className="absolute left-1/2 top-full z-[60] mt-2 -translate-x-1/2 pointer-events-none animate-[fadeUp_.18s_ease-out_both]" data-testid={`${testId}-zoom`} role="tooltip">
    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-2 w-[320px] max-w-[85vw]">
      <img src={src} alt={title} className="w-full h-[210px] object-contain no-placeholder" data-testid={`${testId}-zoom-image`} />
      <p className="text-center text-xs font-semibold text-gray-800 mt-1 truncate">{title}</p>
    </div>
  </div>
);

const VehicleBrandLogo = ({ brand, model, size = 40, className = '', testId = 'vehicle-brand-logo', bare = false, fallbackClass = 'text-gray-600', preferModel = false, zoom = false }) => {
  const brandLogo = useVehicleBrandLogo(brand);
  const [broken, setBroken] = useState(false);
  const [open, setOpen] = useState(false);
  const modelImage = useVehicleModelImage(brand, model, preferModel || !brandLogo || broken);
  const logo = preferModel && modelImage ? modelImage : (brandLogo && !broken ? brandLogo : modelImage);
  const isModel = !!logo && logo === modelImage && (preferModel || !brandLogo || broken);
  useEffect(() => { setBroken(false); }, [brandLogo]);
  const box = { width: size, height: size };
  const kind = isModel ? 'model' : 'brand';
  if (bare) {
    if (!logo) return <Car size={size} className={`shrink-0 ${fallbackClass} ${className}`} data-testid={`${testId}-fallback`} />;
    return <span style={box} className={`inline-flex items-center justify-center shrink-0 rounded-full bg-white p-[2px] ${className}`} data-testid={testId} data-kind={kind}><img src={logo} alt={brand} title={model ? `${brand} ${model}` : brand} onError={() => !isModel && setBroken(true)} className="w-full h-full object-contain no-placeholder" data-testid={`${testId}-image`} /></span>;
  }
  if (!logo) {
    return <div style={box} className={`rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 ${className}`} data-testid={`${testId}-fallback`}><Car size={size / 2} className="text-gray-600" /></div>;
  }
  const zoomable = zoom && isModel;
  return (
    <div style={isModel ? { width: Math.round(size * 1.5), height: size } : box}
      onMouseEnter={zoomable ? () => setOpen(true) : undefined} onMouseLeave={zoomable ? () => setOpen(false) : undefined}
      onFocus={zoomable ? () => setOpen(true) : undefined} onBlur={zoomable ? () => setOpen(false) : undefined}
      onTouchStart={zoomable ? () => setOpen(o => !o) : undefined} tabIndex={zoomable ? 0 : undefined}
      className={`${zoomable ? 'relative' : ''} group ${isModel ? 'rounded-lg' : 'rounded-full'} bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 ${isModel ? 'p-0.5' : 'p-1.5 overflow-hidden'} ${zoomable ? 'cursor-zoom-in hover:border-orange-300 hover:shadow-md transition-[box-shadow,border-color]' : 'overflow-hidden'} ${className}`} data-testid={testId} data-kind={kind} data-zoom={zoomable ? 'true' : undefined}>
      <img src={logo} alt={model ? `${brand} ${model}` : brand} title={model ? `${brand} ${model}` : brand} onError={() => !isModel && setBroken(true)} className={`w-full h-full object-contain no-placeholder ${zoomable ? 'transition-transform duration-300 group-hover:scale-110' : ''}`} data-testid={`${testId}-image`} />
      {zoomable && <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-white/90 shadow flex items-center justify-center text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"><ZoomIn size={12} /></span>}
      {open && zoomable && <HoverPreview src={logo} title={`${brand} ${model}`} testId={testId} />}
    </div>
  );
};

export default VehicleBrandLogo;
